import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { medicalEquipmentService } from '../../../../services/medicalEquipment';
import { tasksService } from '../../../../services/tasks';

import { ms, s, vs } from '../../../../utils/responsive';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicalEquipmentLockUnlock');

type RouteParams = {
  MedicalEquipmentLockUnlock: {
    medicalEquipmentId: string;
  };
};

export const MedicalEquipmentLockUnlockScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<RouteParams, 'MedicalEquipmentLockUnlock'>>();
  const medicalEquipmentId = route.params?.medicalEquipmentId;
  const [submitting, setSubmitting] = useState(false);

  const handleLockPress = async () => {
    try {
      if (!medicalEquipmentId) {
        Alert.alert('Error', 'Missing medical equipment ID');
        return;
      }
      setSubmitting(true);
      logger.debug('[Lock Debug] Starting lock process for medicalEquipmentId:', medicalEquipmentId);

      // Lock the data using the backend endpoint
      await medicalEquipmentService.lockMedicalEquipment(medicalEquipmentId);

      // CRITICAL: Mark task as completed
      await tasksService.completeTask('medical_equipment');

      logger.debug('[Lock Debug] Lock API call completed successfully');
      navigation.navigate('MedicalEquipmentLocked');
    } catch (e) {
      logger.error('[Lock Debug] Error during lock:', e);
      Alert.alert('Error', 'Failed to lock medical equipment information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlockPress = async () => {
    if (!medicalEquipmentId) {
      Alert.alert('Error', 'Missing medical equipment ID');
      return;
    }

    try {
      setSubmitting(true);

      // Mark task as completed
      await tasksService.completeTask('medical_equipment');

      navigation.navigate('MedicalEquipmentUnlocked', { medicalEquipmentId });
    } catch (error) {
      logger.error('Error completing task:', error);
      // Still navigate even if task completion fails
      navigation.navigate('MedicalEquipmentUnlocked', { medicalEquipmentId });
    } finally {
      setSubmitting(false);
    }
  };

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.content}>
          <Text style={styles.title}>Medical Equipment Information Completed</Text>


          <View style={styles.blueContainer}>
            <Text style={styles.messageText}>
              You're all set! Your Medical Equipment has been successfully entered! Would like to share this information with your successor or Key Holders?
            </Text>
          </View>

          <View style={styles.grayButtonContainer}>
            {submitting ? (
              <ActivityIndicator size="large" color="#1C2C4F" />
            ) : null}
            <TouchableOpacity
              style={[styles.button, styles.lockButton]}
              onPress={handleLockPress}
              disabled={submitting}
            >
              <Image
                source={require('../../../../assets/icons/BlueLock.png')}
                style={styles.buttonIcon}
              />
              <Text style={[styles.buttonText, styles.lockText]}>Keep it locked!</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.unlockButton]}
              onPress={handleUnlockPress}
              disabled={submitting}
            >
              <Image
                source={require('../../../../assets/icons/BlueUnlock.png')}
                style={styles.buttonIcon}
              />
              <Text style={[styles.buttonText, styles.unlockText]}>I Want to Unlock it!</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#F3F6F8',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 15,
      paddingTop: ms(15),
      position: 'relative',
    },
    backButton: {
      position: 'absolute',
      left: 50,
      top: '25%',
      transform: [{ translateY: -12 }],
      padding: 5,
      zIndex: 1,
    },
    backIcon: {
      width: 32,
      height: 32,
      tintColor: '#333',
    },
    logo: {
      width: ms(80, 0.3),
      height: ms(80, 0.3),
    },
    profileContainer: {
      position: 'absolute',
      right: 15,
      top: '50%',
      transform: [{ translateY: -12 }],
      width: 45,
      height: 45,
      borderRadius: 22.5,
      backgroundColor: '#fff',
      padding: 2,
    },
    profileImage: {
      width: '100%',
      height: '100%',
      borderRadius: 21,
    },
    content: {
      flex: 1,
      justifyContent: 'space-between',
    },
    title: {
      fontSize: ms(22, 0.3),
      fontWeight: 'bold',
      color: '#1C2C4F',
      textAlign: 'center',
      marginTop: 20,
      paddingHorizontal: 20,
    },
    subtitle: {
      fontSize: 24,
      color: '#1C2C4F',
      textAlign: 'center',
      marginTop: 5,
    },
    blueContainer: {
      backgroundColor: '#1C2C4F',
      padding: 20,
      paddingBottom: 230,
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      position: 'absolute',
      bottom: ms(120),
      left: 0,
      right: 0,
      zIndex: 1,
    },
    messageText: {
      color: '#FFFFFF',
      fontSize: ms(18, 0.3),
      lineHeight: 30,
      textAlign: 'center',
      paddingHorizontal: 25,
    },
    grayButtonContainer: {
      backgroundColor: '#F3F6F8',
      padding: 20,
      paddingTop: ms(30),
      paddingBottom: ms(60),
      borderRadius: 30,
      gap: ms(25),
      position: 'absolute',
      bottom: 20,
      left: 0,
      right: 0,
      zIndex: 2,
    },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: 10,
      paddingLeft: 25,
      borderRadius: 50,
      gap: 50,
    },
    buttonIcon: {
      width: 50,
      height: 50,
      resizeMode: 'contain',
    },
    buttonText: {
      fontSize: ms(18, 0.3),
      fontWeight: '600',
      alignSelf: 'center',
    },
    lockButton: {
      backgroundColor: '#FF9F5A',
    },
    unlockButton: {
      backgroundColor: '#29BFC0',
    },
    lockText: {
      color: '#FFFFFF',
    },
    unlockText: {
      color: '#FFFFFF',
    },
  });
